import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { AbigailBackdrop } from "@/components/abigail/AbigailBackdrop";
import { Wordmark } from "@/components/abigail/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { useAuthActions } from "@/lib/auth";
import { PASSWORD_MIN_LENGTH, validateStrongPassword } from "@shared/password";

export const Route = createFileRoute("/_authenticated/definir-senha")({
  component: DefinirSenhaPage,
});

function DefinirSenhaPage() {
  const navigate = useNavigate();
  const { refreshMe } = useAuthActions();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const check = validateStrongPassword(password, confirm);
    if (!check.ok) {
      setError(check.message ?? "Senha inválida.");
      return;
    }

    setSaving(true);
    try {
      await api.post("/api/auth/password", { password });
      await refreshMe();
      void navigate({ to: "/bem-vindo", replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Não foi possível trocar a senha agora.";
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4 py-12">
      <AbigailBackdrop />

      <section className="relative w-full max-w-md rounded-2xl border border-border bg-card px-7 py-9 shadow-card-deep backdrop-blur-md">
        <div className="flex flex-col items-center text-center">
          <Wordmark className="text-2xl" />
          <ShieldCheck className="text-accent mt-6 h-7 w-7" aria-hidden />
          <h1 className="font-serif text-ember mt-4 text-3xl">Crie sua senha</h1>
          <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
            Este é seu primeiro acesso. Defina uma senha pessoal para substituir a senha inicial
            enviada por e-mail.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-7 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-muted-foreground text-xs">
              Nova senha
            </Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              maxLength={200}
              onChange={(event) => setPassword(event.target.value)}
              className="border-border bg-background/60 h-11 text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-muted-foreground text-xs">
              Confirmar nova senha
            </Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              maxLength={200}
              onChange={(event) => setConfirm(event.target.value)}
              className="border-border bg-background/60 h-11 text-sm"
            />
          </div>

          <p className="text-muted-foreground text-xs">
            Mínimo de {PASSWORD_MIN_LENGTH} caracteres, com maiúscula, minúscula, número e
            caractere especial.
          </p>

          {error ? (
            <p role="alert" className="text-destructive text-xs">
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            disabled={saving}
            className="bg-ember shadow-ember h-11 w-full text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar e continuar"}
          </Button>
        </form>
      </section>
    </div>
  );
}
